import { ExternalLink, FileDown, RefreshCw, X } from "lucide-react";
import { useNotify } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { formatEuro } from "./gulaConstants";
import type { GulaOrder } from "./gulaTypes";
import {
  PACKLINK_APP_URL,
  UNPAID_STATES,
  cancelPacklink,
  isPacklinkProblem,
  packlinkStateLabel,
  refreshPacklink,
} from "./packlink";

/** Status of the order's Packlink shipment, with its quick actions. */
export const PacklinkPanel = ({
  order,
  onRefresh,
}: {
  order: GulaOrder;
  onRefresh: () => void;
}) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const unpaid =
    !order.packlink_state || UNPAID_STATES.includes(order.packlink_state);

  const run = async (action: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await action();
      notify(done, { type: "info" });
      onRefresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Error de Packlink", {
        type: "error",
      });
    } finally {
      setBusy(false);
      setConfirmCancel(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5 rounded-md bg-muted/50 p-2 text-xs">
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium text-foreground">
          {order.packlink_service ?? "Packlink"}
        </span>
        <button
          type="button"
          aria-label="Actualizar estado de Packlink"
          title="Actualizar estado"
          disabled={busy}
          onClick={() =>
            run(() => refreshPacklink(order.id), "Estado actualizado")
          }
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} />
        </button>
      </div>
      <span
        className={cn(
          isPacklinkProblem(order.packlink_state)
            ? "font-medium text-destructive"
            : "text-muted-foreground",
        )}
      >
        {packlinkStateLabel(order.packlink_state)}
        {order.shipping_cost != null
          ? ` · ${formatEuro(Number(order.shipping_cost))} sin IVA`
          : ""}
      </span>
      {order.tracking_number ? (
        <span className="break-all text-muted-foreground">
          Seguimiento: {order.tracking_number}
        </span>
      ) : null}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <a
          href={PACKLINK_APP_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-medium text-foreground hover:underline"
        >
          {unpaid ? "Pagar en Packlink" : "Ver en Packlink"}
          <ExternalLink className="h-3 w-3" />
        </a>
        {order.packlink_label_url ? (
          <a
            href={order.packlink_label_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 hover:underline"
          >
            Etiqueta <FileDown className="h-3 w-3" />
          </a>
        ) : null}
        {order.packlink_tracking_url && order.tracking_number ? (
          <a
            href={order.packlink_tracking_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 hover:underline"
          >
            Web del transportista <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>
      {unpaid ? (
        <Button
          size="sm"
          variant={confirmCancel ? "destructive" : "ghost"}
          className="h-7 justify-start px-1 text-xs"
          disabled={busy}
          onClick={() =>
            confirmCancel
              ? run(
                  () => cancelPacklink(order.id),
                  "Envío de Packlink cancelado",
                )
              : setConfirmCancel(true)
          }
        >
          <X className="h-3.5 w-3.5" />
          {confirmCancel ? "Pulsa otra vez para cancelarlo" : "Cancelar envío"}
        </Button>
      ) : null}
    </div>
  );
};
